import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { Loader2, Wallet, ArrowUpRight, Clock, CheckCircle2 } from "lucide-react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { PayPalLogo, VenmoLogo, AppleLogo } from "@/components/PaymentIcons";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";

const METHODS = [
  { key: "venmo", label: "Venmo", Logo: VenmoLogo },
  { key: "paypal", label: "PayPal", Logo: PayPalLogo },
  { key: "applepay", label: "Apple Cash", Logo: AppleLogo },
];

export const PayoutCard = ({ handles = {} }) => {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [amount, setAmount] = useState("");
  const [method, setMethod] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(() => {
    api.get("/payouts/mine").then(({ data }) => setData(data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const available = data?.available_balance ?? 0;
  const availableMethods = METHODS.filter((m) => handles[m.key]);

  const openDialog = () => {
    setAmount(available > 0 ? String(available) : "");
    setMethod(availableMethods[0]?.key || "");
    setOpen(true);
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      await api.post("/payouts/request", { amount: parseFloat(amount), method });
      toast.success("Cash out requested! You'll be paid shortly.");
      setOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not request payout");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="mt-4 rounded-2xl border border-white/10 bg-[#0F0F0F] p-6 sm:p-8" data-testid="payout-card">
      <div className="flex flex-col gap-6 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">
            <Wallet className="h-4 w-4" /> Available to cash out
          </div>
          <div data-testid="payout-balance" className="mt-3 font-display text-4xl font-black text-[#00E676]">
            ${available.toFixed(2)}
          </div>
        </div>

        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <button
              data-testid="cashout-btn"
              onClick={openDialog}
              disabled={available <= 0 || availableMethods.length === 0}
              className="flex items-center gap-2 rounded-full bg-[#00E676] px-7 py-3.5 font-bold text-black transition-transform active:scale-95 disabled:cursor-not-allowed disabled:opacity-40"
            >
              <ArrowUpRight className="h-5 w-5" /> Cash out
            </button>
          </DialogTrigger>
          <DialogContent data-testid="cashout-dialog" className="border-white/10 bg-[#0F0F0F] text-white">
            <DialogHeader>
              <DialogTitle className="font-display text-2xl font-bold">Cash out your tips</DialogTitle>
            </DialogHeader>
            <div className="mt-2 space-y-5">
              <div>
                <label className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-400">Amount</label>
                <div className="mt-2 flex items-center gap-2 rounded-xl border border-white/10 bg-[#050505] px-4">
                  <span className="font-display text-xl font-bold text-zinc-500">$</span>
                  <Input
                    data-testid="cashout-amount-input"
                    type="number"
                    min="1"
                    max={available}
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    className="border-0 bg-transparent text-xl font-bold focus-visible:ring-0"
                  />
                </div>
                <p className="mt-1 text-xs text-zinc-500">Max ${available.toFixed(2)}</p>
              </div>

              <div>
                <label className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-400">Pay me via</label>
                <div className="mt-2 grid gap-2">
                  {availableMethods.map(({ key, label, Logo }) => (
                    <button
                      key={key}
                      data-testid={`cashout-method-${key}`}
                      onClick={() => setMethod(key)}
                      className={`flex items-center justify-between rounded-xl border px-4 py-3 transition-colors ${
                        method === key
                          ? "border-[#00E676] bg-[#00E676]/10"
                          : "border-white/10 bg-[#050505] hover:border-white/25"
                      }`}
                    >
                      <span className="flex items-center gap-3">
                        <Logo className="h-5 w-5" /> {label}
                      </span>
                      <span className="font-mono-tip text-sm text-zinc-400">{handles[key]}</span>
                    </button>
                  ))}
                </div>
              </div>

              <button
                data-testid="cashout-submit-btn"
                onClick={submit}
                disabled={submitting || !amount || !method || parseFloat(amount) <= 0}
                className="flex h-12 w-full items-center justify-center rounded-full bg-[#00E676] font-bold text-black transition-transform active:scale-95 disabled:opacity-60"
              >
                {submitting ? <Loader2 className="h-5 w-5 animate-spin" /> : "Request cash out"}
              </button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {data?.requests?.length > 0 && (
        <div className="mt-6 border-t border-white/10 pt-5">
          <p className="mb-3 text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">Payout history</p>
          <div className="space-y-2" data-testid="payout-history">
            {data.requests.map((r) => (
              <div
                key={r.id}
                className="flex items-center justify-between rounded-xl border border-white/10 bg-[#050505] px-4 py-3"
              >
                <div className="flex items-center gap-3">
                  <span className="font-display text-lg font-bold">${r.amount.toFixed(2)}</span>
                  <span className="text-sm capitalize text-zinc-500">{r.method}</span>
                </div>
                {r.status === "paid" ? (
                  <span className="flex items-center gap-1.5 text-sm font-semibold text-[#00E676]">
                    <CheckCircle2 className="h-4 w-4" /> Paid
                  </span>
                ) : (
                  <span className="flex items-center gap-1.5 text-sm font-semibold text-amber-400">
                    <Clock className="h-4 w-4" /> Pending
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
